import React, { useMemo } from 'react'

/**
 * Two-thumb range slider for numeric values, with paired number inputs.
 *
 * Props:
 *   min, max, step (numeric bounds for the slider track)
 *   low, high      (current values, can be null/undefined → treated as bounds)
 *   onChange({ low, high })
 *   formatValue    (optional: format number → display string in chips, e.g. "$100")
 */
export default function RangeSlider({
    min = 0,
    max = 5000,
    step = 25,
    low,
    high,
    onChange,
    formatValue = (v) => v,
}) {
    const lowValue = low == null || low === '' ? min : Number(low)
    const highValue = high == null || high === '' ? max : Number(high)

    const safeLow = Math.max(min, Math.min(lowValue, highValue))
    const safeHigh = Math.min(max, Math.max(highValue, lowValue))

    const fillStyle = useMemo(() => {
        const span = max - min || 1
        const left = ((safeLow - min) / span) * 100
        const right = ((safeHigh - min) / span) * 100
        return { left: `${left}%`, width: `${right - left}%` }
    }, [safeLow, safeHigh, min, max])

    const setLow = (v) => {
        const next = Math.min(Number(v), safeHigh)
        onChange({ low: next === min ? '' : next, high: high ?? '' })
    }
    const setHigh = (v) => {
        const next = Math.max(Number(v), safeLow)
        onChange({ low: low ?? '', high: next === max ? '' : next })
    }

    return (
        <div className="cd-range">
            <div className="cd-range-track">
                <div className="cd-range-fill" style={fillStyle} />
                <input
                    type="range"
                    min={min}
                    max={max}
                    step={step}
                    value={safeLow}
                    onChange={(e) => setLow(e.target.value)}
                    aria-label="Minimum value"
                />
                <input
                    type="range"
                    min={min}
                    max={max}
                    step={step}
                    value={safeHigh}
                    onChange={(e) => setHigh(e.target.value)}
                    aria-label="Maximum value"
                />
            </div>
            <div className="cd-range-values">
                <div className="cd-range-input">
                    <span className="prefix">$</span>
                    <input
                        type="number"
                        min={min}
                        max={max}
                        step={step}
                        value={low ?? ''}
                        placeholder={String(min)}
                        onChange={(e) => onChange({ low: e.target.value, high: high ?? '' })}
                        aria-label="Minimum"
                    />
                </div>
                <span className="cd-range-dash">—</span>
                <div className="cd-range-input">
                    <span className="prefix">$</span>
                    <input
                        type="number"
                        min={min}
                        max={max}
                        step={step}
                        value={high ?? ''}
                        placeholder={String(max) + '+'}
                        onChange={(e) => onChange({ low: low ?? '', high: e.target.value })}
                        aria-label="Maximum"
                    />
                </div>
            </div>
            <div className="cd-range-legend">
                {formatValue(safeLow)} – {formatValue(safeHigh)}
                {safeHigh >= max && '+'}
            </div>
        </div>
    )
}
